
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  rotation: number;
  scale: number;
}

interface ConfettiEffectProps {
  isActive: boolean;
  duration?: number;
}

const COLORS = [
  'hsl(var(--primary))',
  '#FFD700', // Gold
  '#FF6B6B', // Coral
  '#4ECDC4', // Teal
  '#45B7D1', // Sky Blue
  '#96CEB4', // Mint
  '#FFEAA7', // Light Yellow
  '#DDA0DD', // Plum
];

export const ConfettiEffect: React.FC<ConfettiEffectProps> = ({ 
  isActive, 
  duration = 3000 
}) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  const generateConfetti = useCallback(() => {
    const newPieces: ConfettiPiece[] = [];
    const pieceCount = 50;

    for (let i = 0; i < pieceCount; i++) {
      newPieces.push({
        id: i,
        x: Math.random() * 100,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        delay: Math.random() * 0.5,
        rotation: Math.random() * 360,
        scale: 0.5 + Math.random() * 0.5,
      });
    }

    setPieces(newPieces);
  }, []);

  useEffect(() => {
    if (isActive) {
      generateConfetti();
      const timer = setTimeout(() => {
        setPieces([]);
      }, duration);
      return () => clearTimeout(timer);
    } else {
      setPieces([]);
    }
  }, [isActive, duration, generateConfetti]);

  return (
    <AnimatePresence>
      {pieces.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          {pieces.map((piece) => (
            <motion.div
              key={piece.id}
              initial={{
                x: `${piece.x}vw`,
                y: -20,
                rotate: 0,
                opacity: 1,
              }}
              animate={{
                y: '110vh',
                rotate: piece.rotation + 720,
                opacity: [1, 1, 0],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 2.5 + Math.random(),
                delay: piece.delay,
                ease: 'easeOut',
              }}
              style={{
                position: 'absolute',
                width: 10 * piece.scale,
                height: 10 * piece.scale,
                backgroundColor: piece.color,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};
